import { useAgeGate } from '@/contexts/AgeGateContext';
import { useBranding } from '@/contexts/BrandingContext';
import { Compass, ArrowLeft, Gamepad2 } from 'lucide-react';

// Under 21 fallback page - shown when user fails age verification
export default function Under21Page() {
  const { clearUnder21 } = useAgeGate();
  const { brandName } = useBranding();

  const handleReturn = () => {
    // Clear under-21 state and redirect to a safe location
    clearUnder21();
    window.location.href = 'https://www.google.com';
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Background - Same as age gate */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #2d1810 0%, #1a1a1a 50%, #2d2418 100%)',
        }}
      />
      
      {/* Texture overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Dark overlay - slightly more dimmed */}
      <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm" />

      {/* Content Container */}
      <div 
        className="relative w-full max-w-md mx-auto animate-in fade-in zoom-in-95 duration-300"
        style={{ maxHeight: '90vh' }}
      >
        <div 
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #faf8f5 0%, #f5f2ed 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Inner texture */}
          <div 
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <div className="relative p-8 sm:p-10 text-center">
            {/* Logo/Brand Mark - Dimmed */}
            <div className="flex justify-center mb-6">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #8a8a8a 0%, #9a9a9a 50%, #aaa 100%)',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Compass className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
            </div>

            {/* Message */}
            <h1 
              className="mb-4"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#1a1a1a',
                letterSpacing: '-0.01em',
              }}
            >
              We&apos;re sorry
            </h1>

            <p 
              className="mb-8"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: '1rem',
                color: '#5a5a5a',
                lineHeight: 1.6,
              }}
            >
              You must be 21 or older to explore {brandName}.
            </p>

            {/* Divider */}
            <div 
              className="w-16 h-px mx-auto mb-8"
              style={{ background: '#d0ccc5' }}
            />

            {/* Play Ms. Pac-Man Button */}
            <a
              href="https://www.miniplay.com/game/ms-pac-man"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 mb-3 w-full max-w-xs mx-auto"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: '0.9375rem',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                border: 'none',
                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(245, 158, 11, 0.3)';
              }}
              data-testid="under21-pacman-btn"
            >
              <Gamepad2 className="w-5 h-5" />
              Play Ms. Pac-Man Instead!
            </a>

            {/* Leave Site Button */}
            <button
              onClick={handleReturn}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: '0.9375rem',
                background: '#f0ede8',
                color: '#4a4a4a',
                border: '1px solid #d0ccc5',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e8e5e0';
                e.currentTarget.style.borderColor = '#c0bbb3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f0ede8';
                e.currentTarget.style.borderColor = '#d0ccc5';
              }}
              data-testid="under21-return-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Leave Site
            </button>

            {/* Legal Notice */}
            <p 
              className="mt-8"
              style={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: '0.6875rem',
                color: '#9a9a9a',
                lineHeight: 1.5,
              }}
            >
              This site is intended for adults of legal drinking age.<br />
              Please drink responsibly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
